import { useCallback, useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { useUser } from "@components/user-context";

import { useFeatureFlags, useUpdateUserFeatureFlag } from "./http";

const V1_TO_V2 = {
  sales: "attribution/sales",
  "creative-analytics": "attribution/creative-analytics",
  "product-analytics": "attribution/product-analytics",
  orders: "attribution/orders",
  overview: "home/overview",
} as const;

type V1Keys = keyof typeof V1_TO_V2;
type V2Values = (typeof V1_TO_V2)[V1Keys];

export const useToggleNBV2 = () => {
  const { hasFeatureFlag } = useFeatureFlags();
  const [updateFeatureFlag] = useUpdateUserFeatureFlag();
  const { user } = useUser();

  const dashboardV2 = useMemo(() => {
    return hasFeatureFlag("enableDashboardV2");
  }, [hasFeatureFlag]);

  const V2_TO_V1 = useMemo(() => {
    return Object.entries(V1_TO_V2).reduce((acc, [v1, v2]) => {
      acc[v2] = v1 as V1Keys;
      return acc;
    }, {} as Record<V2Values, V1Keys>);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(async () => {
    await updateFeatureFlag("enableDashboardV2", !dashboardV2);
    const pathname = location.pathname.slice(1);

    const excludingUserIdPath = pathname.substring(pathname.indexOf("/") + 1);
    let newPath = excludingUserIdPath;

    if (dashboardV2) {
      Object.entries(V2_TO_V1).forEach(([v2Path, v1Path]) => {
        if (excludingUserIdPath.startsWith(v2Path)) {
          newPath = excludingUserIdPath.replace(v2Path, v1Path);
          return;
        }
      });
    } else {
      Object.keys(V1_TO_V2).forEach((key) => {
        if (excludingUserIdPath.startsWith(key)) {
          newPath = excludingUserIdPath.replace(
            key,
            V1_TO_V2[key as keyof typeof V1_TO_V2],
          );
          return;
        }
      });
    }
    navigate(`/${user.displayUserId}${newPath ? `/${newPath}` : ""}`);
    window.location.reload();
  }, [
    updateFeatureFlag,
    dashboardV2,
    location.pathname,
    navigate,
    user.displayUserId,
    V2_TO_V1,
  ]);
};
